import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import '../styles/legal.css';

const PrivacyPage = () => {
  return (
    <div className="legal-page">
      <div className="legal-header">
        <div className="legal-header-content">
          <h1 className="legal-title">Privacy Policy</h1>
          <p className="legal-updated">Last updated: April 30, 2026</p>
        </div>
      </div>

      <div className="legal-content">
        <section className="legal-section">
          <h2>1. Who We Are</h2>
          <p>
            Core Air Life Fitness (dba GoCalFit, Cal Fit) ("we," "us," or "our") provides boutique fitness experiences including bungee fitness, aerial programs for kids, camps, and coaching services in Wilmington, NC and surrounding areas.
          </p>
          <p className="legal-highlight">
            We collect only what we need to run a safe, smooth, human-centered experience.
          </p>
        </section>

        <section className="legal-section">
          <h2>2. What We Collect</h2>
          <p>When you interact with us (website, bookings, forms, emails, etc.), we may collect:</p>
          
          <h3>Basic Info</h3>
          <ul>
            <li>Name</li>
            <li>Email</li>
            <li>Phone number</li>
            <li>Address (if needed for purchases/shipping)</li>
          </ul>

          <h3>Booking + Payment Info</h3>
          <ul>
            <li>Class registrations</li>
            <li>Purchase history</li>
            <li>Payment details (processed securely through third parties — we do not store full card details)</li>
          </ul>

          <h3>Health + Safety Info (when relevant)</h3>
          <ul>
            <li>Injuries, conditions, or limitations</li>
            <li>Emergency contact info</li>
            <li>Minor participant information (provided by parent/guardian)</li>
          </ul>

          <h3>Optional / Voluntary Info</h3>
          <ul>
            <li>Messages, responses, preferences</li>
            <li>Anything you choose to share with us</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>3. Why We Collect It</h2>
          <p>We use your information to:</p>
          <ul>
            <li>Book and manage classes, camps, and sessions</li>
            <li>Communicate with you (confirmations, updates, reminders)</li>
            <li>Keep you (and your kids) safe during participation</li>
            <li>Process payments</li>
            <li>Improve our services and offerings</li>
            <li>Occasionally share relevant updates, events, or offers</li>
          </ul>
          <p className="legal-highlight">
            We're not here to over-collect. If we don't need it, we don't ask for it.
          </p>
        </section>

        <section className="legal-section">
          <h2>4. How We Share Information</h2>
          <p>We only share your information when necessary to run the business, including:</p>
          <ul>
            <li>Payment processors (Stripe, PayPal)</li>
            <li>Scheduling platforms (Acuity)</li>
            <li>Form/registration tools (Jotform)</li>
            <li>Email and communication tools</li>
          </ul>
          <p>These providers handle data securely under their own privacy policies.</p>
          <p className="legal-emphasis">We do not sell your data. Ever.</p>
        </section>

        <section className="legal-section">
          <h2>5. Cookies + Website Data</h2>
          <p>Our website may use basic cookies or analytics tools to understand traffic and improve experience.</p>
          <p className="legal-highlight">Nothing creepy. No aggressive tracking.</p>
          <p>You can disable cookies in your browser settings.</p>
        </section>

        <section className="legal-section">
          <h2>6. Children's Privacy</h2>
          <p>For youth programs, we collect information only from parents/guardians.</p>
          <p>We use that information strictly for:</p>
          <ul>
            <li>Safety</li>
            <li>Communication</li>
            <li>Program participation</li>
          </ul>
          <p>We do not knowingly collect data directly from children without parental consent.</p>
        </section>

        <section className="legal-section">
          <h2>7. Data Security</h2>
          <p>We take reasonable steps to protect your information.</p>
          <p>That said, no system is 100% secure. By using our services, you understand this risk.</p>
        </section>

        <section className="legal-section">
          <h2>8. How Long We Keep Data</h2>
          <p>We keep your information only as long as necessary to:</p>
          <ul>
            <li>Provide services</li>
            <li>Meet legal/financial requirements</li>
            <li>Maintain business records</li>
          </ul>
          <p>Then we delete or anonymize it.</p>
        </section>

        <section className="legal-section">
          <h2>9. Your Rights</h2>
          <p>You can:</p>
          <ul>
            <li>Request access to your data</li>
            <li>Ask us to update or delete your information</li>
            <li>Opt out of marketing emails anytime</li>
          </ul>
          <p>Just email: <a href="mailto:hello@gocalfit.com">hello@gocalfit.com</a></p>
        </section>

        <section className="legal-section legal-philosophy">
          <h2>10. Philosophy (The Human Part)</h2>
          <p>We built this business around trust.</p>
          <p>That means:</p>
          <ul>
            <li>No overcomplication</li>
            <li>No hidden data games</li>
            <li>No treating you like a data point</li>
          </ul>
          <p>You already have what you need to start — we're just here to support the experience.</p>
          <p className="legal-highlight">Your information is part of that trust. We treat it that way.</p>
        </section>

        <section className="legal-section">
          <h2>11. Updates</h2>
          <p>We may update this policy as things evolve.</p>
          <p>If we make meaningful changes, we'll reflect that here.</p>
        </section>

        <section className="legal-section">
          <h2>12. Contact</h2>
          <p>Questions? Concerns? Want your data removed?</p>
          <p><a href="mailto:hello@gocalfit.com" className="legal-contact-link">hello@gocalfit.com</a></p>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPage;
